import { useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  mapPromotionalAdError,
  useAuthSession,
  useCreatePromotionalAd,
  useStorePromotionalAds,
  useTranslation,
  type PromotionalAdErrorKey,
  type StorePromotionalAdStoreContext,
  type TranslationKey,
} from '@dukanti/shared';
import { supabase } from '@/lib/supabase';
import { useCurrentStore } from '@/hooks/use-current-store';
import {
  PromotionalAdFormPage,
  type PromotionalAdFormSubmit,
} from '@/features/promotional-ads/components/promotional-ad-form-page';
import { usePromotionalAdsAccess } from '@/features/promotional-ads/hooks/use-promotional-ads-access';

function toStoreContext(
  storeId: string | null,
  storeSlug: string | null,
): StorePromotionalAdStoreContext | null {
  if (!storeId || !storeSlug) return null;
  return { storeId, storeSlug };
}

export function PromotionalAdNewRoute() {
  const { t, dir } = useTranslation();
  const navigate = useNavigate();
  const authSession = useAuthSession(supabase);
  const { storeId, storeSlug } = useCurrentStore(authSession);
  const storeContext = useMemo(
    () => toStoreContext(storeId, storeSlug ?? null),
    [storeId, storeSlug],
  );
  // Epic #228 P7: creating an ad is legacy-only authoring.
  const access = usePromotionalAdsAccess();
  const isAuthoring = access.state === 'authoring';
  const adsQuery = useStorePromotionalAds(supabase, isAuthoring ? storeContext : null);
  const createAd = useCreatePromotionalAd(supabase);
  const [submitError, setSubmitError] = useState<PromotionalAdErrorKey | null>(null);

  async function handleSubmit(values: PromotionalAdFormSubmit): Promise<void> {
    setSubmitError(null);
    if (!storeContext) {
      setSubmitError('admin.promotionalAds.errors.permissionDenied');
      return;
    }
    if (!values.image) {
      setSubmitError('admin.promotionalAds.errors.mediaRequired');
      return;
    }

    try {
      await createAd.mutateAsync({
        store_id: storeContext.storeId,
        store_slug: storeContext.storeSlug,
        title: values.title,
        alt_text: values.alt_text,
        destination: values.destination,
        is_active: values.is_active,
        image: values.image,
      });
      toast.success(t('admin.promotionalAds.toasts.created' as TranslationKey));
      navigate('/promotions', { replace: true });
    } catch (err) {
      const mapped = mapPromotionalAdError(err);
      setSubmitError(mapped);
      toast.error(t(mapped as TranslationKey));
    }
  }

  if (access.state !== 'authoring' && access.state !== 'loading') {
    return <Navigate to="/promotions" replace />;
  }

  if (access.state === 'loading') {
    return (
      <main
        dir={dir}
        data-testid="promotional-ad-new-route"
        aria-busy="true"
        className="bg-background min-h-screen"
      >
        <span role="status" className="sr-only">
          {t('common.loading')}
        </span>
      </main>
    );
  }

  return (
    <main dir={dir} data-testid="promotional-ad-new-route" className="bg-background min-h-screen">
      <PromotionalAdFormPage
        mode="create"
        storeId={storeContext?.storeId ?? null}
        storeSlug={storeContext?.storeSlug ?? ''}
        activeAdCount={adsQuery.ads.filter((ad) => ad.is_active).length}
        isSubmitting={createAd.isPending || !storeContext}
        errorKey={submitError}
        onSubmit={handleSubmit}
        onCancel={() => navigate('/promotions')}
      />
    </main>
  );
}
